import { v4 as uuidv4 } from 'uuid';
import { Notification, NotificationType, NotificationData } from '../types/notifications';

// Local storage key for notifications
const NOTIFICATIONS_STORAGE_KEY = 'app_notifications';

/**
 * Service for managing notifications
 */
export const notificationsService = {
  /**
   * Get all notifications for the current user
   */
  getNotifications(): Notification[] {
    try {
      const storedNotifications = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
      if (!storedNotifications) {
        return [];
      }
      
      return JSON.parse(storedNotifications) as Notification[];
    } catch (error) {
      console.error('Error getting notifications from local storage:', error);
      return [];
    }
  },

  /**
   * Add a new notification
   * @param type The notification type
   * @param data The notification data
   * @returns The created notification
   */
  addNotification(type: NotificationType, data: NotificationData): Notification {
    try {
      const notifications = this.getNotifications();
      
      const newNotification: Notification = {
        id: uuidv4(),
        type,
        timestamp: Date.now(),
        data
      };
      
      const updatedNotifications = [newNotification, ...notifications];
      localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(updatedNotifications));
      
      return newNotification;
    } catch (error) {
      console.error('Error adding notification to local storage:', error);
      throw error;
    }
  },

  /**
   * Remove a notification by ID
   * @param id The notification ID to remove
   */
  removeNotification(id: string): void {
    try {
      const notifications = this.getNotifications();
      const updatedNotifications = notifications.filter(notification => notification.id !== id);
      localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Error removing notification from local storage:', error);
      throw error;
    }
  },

  /**
   * Clear all notifications
   */
  clearNotifications(): void {
    try {
      localStorage.removeItem(NOTIFICATIONS_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing notifications from local storage:', error);
      throw error;
    }
  },

  /**
   * Create a Good Thing notification
   * @param submitterName The name of the person who submitted the Good Thing
   * @param goodThingId Optional ID of the Good Thing
   * @returns The created notification
   */
  createGoodThingNotification(submitterName: string, goodThingId?: string): Notification {
    return this.addNotification('good-thing', {
      submitterName,
      goodThingId
    });
  }
};
